/* eslint-disable no-secrets/no-secrets */

import React, {
    useContext, useCallback, useState, useEffect, useMemo
} from 'react';
import _ from 'lodash';
import { TranslatorContext } from '@jutro/locale';
import { withViewModelService, ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { wizardProps, WizardContext } from 'wmic-pe-portals-custom-wizard-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useValidation } from '@xengage/gw-portals-validation-react'
import { DropdownMenuButton } from '@jutro/components';
import { useDocumentTitle, useWizardModals, WMICWizardChangeOrRenewalPage } from 'wmic-pe-portals-wizard-components-ui';
import {
    CONSTANTS,
    WMICUserAccessUtil,
    WMICDriverUtil,
    WMICCreditConsentUtil,
    WMICLogger
} from 'wmic-pe-portals-utils-js';
import { WMICDriverDetailView } from 'wmic-pe-capability-gateway-common-pa-react';
import { EntityUtil } from '@xengage/gw-portals-util-js'

import { messages as commonMessages } from '@xengage/gw-platform-translations';
import { messages as policyChangeCommonMessages } from 'wmic-pe-capability-gateway-policychange-common-react';
import metadata from './WMICPCPADriversDetailsPage.metadata.json5';
import messages from './WMICPCPADriversDetailsPage.messages';
import {WMICErrorHandler} from "wmic-pe-capability-quoteandbind-common-react";

import { DropdownMenuLink } from '@jutro/router';

const ANIS_PATH = 'baseData.additionalNamedInsureds_WMIC';
const DRIVERS_PATH = 'lobData.personalAuto.coverables.drivers';
const { INSURED_ROLES } = CONSTANTS;

const createDriver = (index, skeletonStruct, person, insuredRelation) => {
    if (!person) {
        person = {
            tempID: EntityUtil.nextId()
        }
    }

    const driver = {
        person: { ...person },
        firstName: person.firstName,
        middleName: person.middleName,
        lastName: person.lastName,
        displayName: `${person.firstName} ${person.lastName}`,
        dashReports_Ext: [],
        dateOfBirth: person.dateOfBirth,
        driverClassHistory_WMIC: [],
        getStateLicThirtyDay_WMIC: null,
        accountHolder: insuredRelation === INSURED_ROLES.PRIMARY_INSURED,
        mvrReports_WMIC: [],
        mvrReport_WMIC: {
            mvrIncidents_WMIC: []
        },
        licenseCountry: 'CA',
        policyHistory_WMIC: {
            previousInsurances: []
        },
        mvrAddressMatchesInfo: null,
        isPrimaryInsured_WMIC: insuredRelation === INSURED_ROLES.PRIMARY_INSURED,
        isAdditionalNamedInsured_WMIC: insuredRelation === INSURED_ROLES.ADDITIONAL_INSURED,
    };

    // this is the hookup for server aspects
    if (skeletonStruct.length > 0 && skeletonStruct[0].metaDataMap) {
        driver.metaDataMap = skeletonStruct[0].metaDataMap;
    }

    driver.tempID = EntityUtil.nextId();

    if (person) {
        if (person.publicID) {
            _.set(driver, 'person.publicID', person.publicID);
        }

        if (person.primaryAddress) {
            _.set(driver, 'licenseState', person.primaryAddress.state);
        }
    }

    return driver;
};

function WMICPCPADriversDetailsPage(props) {
    const {
        wizardData: policyChangeVM,
        updateWizardData
    } = props;
    const { setWizardLoading, showConfirm, showError } = useWizardModals();
    const { EndorsementService } = useDependencies('EndorsementService');
    const { authHeader, authUserData } = useAuthentication();
    const translator = useContext(TranslatorContext);
    const [skeletonStruct, setSkeletonStruct] = useState({});
    const [additionalNamedInsureds, setAdditionalNamedInsureds] = useState([]);
    const [showErrors, setShowErrors] = useState(false);
    const viewModelService = useContext(ViewModelServiceContext);
    const { initialValidation, isComponentValid, onValidate, registerInitialComponentValidation } = useValidation(
        'WMICPCPADriversDetailsPage'
    );
    const driversWithDuplicateLicence  = WMICDriverUtil.getDriversWithDuplicateLicense(_.get(policyChangeVM.value, DRIVERS_PATH));

    const { maintainFurtherStepsVisitedSubmitted, setIsCustomStepsInvalidated } = useContext(WizardContext);

    useDocumentTitle(translator(messages.driversTitle), policyChangeVM);

    const initDriverData = useCallback(async () => {
        const driversTmp = _.get(policyChangeVM, DRIVERS_PATH);
        const additionalNamedInsuredsSub = _.get(policyChangeVM, `${ANIS_PATH}.value`);
        const additionalNamedInsuredsTmp = [];
        const skeletonStructure = await EndorsementService.getSkeletonStructure(
            policyChangeVM.value,
            authHeader
        );

        // pull out driver specific structure
        const driverStruct = _.get(skeletonStructure, DRIVERS_PATH)

        setSkeletonStruct(driverStruct);

        if (additionalNamedInsuredsSub && !_.isEmpty(additionalNamedInsuredsSub)) {
            additionalNamedInsuredsSub.forEach((nameInsured) => {
                const additionalNameInsured = viewModelService.create(nameInsured, 'pc', 'wmic.edge.ca.capabilities.policyjob.draft.dto.AdditionalNamedInsuredDTO_WMIC');

                _.set(additionalNameInsured, 'isDriver', false);

                const aniSubType = _.get(additionalNameInsured, 'subtype.value.code');

                if (aniSubType === CONSTANTS.Person.toLowerCase()) {
                    const isDriver = driversTmp.value.some((driver) => _.get(additionalNameInsured, 'contactPublicID.value') === _.get(driver, 'person.publicID.value'));

                    _.set(additionalNameInsured, 'isDriver', isDriver);
                }

                additionalNamedInsuredsTmp.push(additionalNameInsured);
            });
            if (!_.isEmpty(additionalNamedInsuredsTmp)) {
                setAdditionalNamedInsureds(additionalNamedInsuredsTmp);
            }
        }

        const primaryNamedInsuredTmp = _.get(policyChangeVM, 'baseData.primaryNamedInsured_WMIC.value');

        const isDriver = driversTmp.value.some((driver) => _.get(primaryNamedInsuredTmp, 'contactPublicID') === _.get(driver, 'person.publicID'));

        _.set(primaryNamedInsuredTmp, 'isDriver', isDriver);
    }, [EndorsementService, authHeader, policyChangeVM, viewModelService])

    useEffect(() => {
        registerInitialComponentValidation(() => _.get(policyChangeVM, DRIVERS_PATH).value.length > 0);

        initDriverData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const canEditPolicyChange = useMemo(() => WMICUserAccessUtil.canEditPolicyChange(authUserData.permission_Ext), [authUserData.permission_Ext]);
    
    const onEditDriver = useCallback(() => {
        _.set(policyChangeVM, 'isEditingPage.value', true);
    }, [policyChangeVM]);

    const onCreateDriver = useCallback((index, namedInsured, insuredRelation) => {
        if (maintainFurtherStepsVisitedSubmitted.flag) {
            maintainFurtherStepsVisitedSubmitted.flag = false;
            setIsCustomStepsInvalidated(true);
        }
        updateWizardData(policyChangeVM);

        const driver = createDriver(index, skeletonStruct, namedInsured, insuredRelation);
        const driversVM = _.get(policyChangeVM, DRIVERS_PATH);
        const { _xCenter, _dtoName } = driversVM;
        const driverVM = viewModelService.create(driver, _xCenter, _dtoName, policyChangeVM.aspects.context());
        const commercialDriverVM = viewModelService.create({}, 'pc', 'wmic.edge.ca.capabilities.policyjob.lob.personalauto.coverables.dto.CommercialDriverDTO_WMIC').value;
        const isDriverPNI = policyChangeVM.baseData.primaryNamedInsured_WMIC.subtype.value === CONSTANTS.Person
            && policyChangeVM.baseData.primaryNamedInsured_WMIC.publicID.value === driverVM.person.publicID.value

        if (_.get(policyChangeVM, 'baseData.isDriveChangeAvailable_WMIC.value') === true) {
            const driveChangeVM = viewModelService.create(
                {}, 'pc', 'wmic.edge.ca.capabilities.policyjob.lob.personalauto.coverables.dto.DriveChangeDTO_WMIC').value;

            if (isDriverPNI) {
                if (_.get(policyChangeVM, 'baseData.isDriveChangeEnrolledInOtherPoliciesForPNI_WMIC.value')) {
                _.set(driveChangeVM, 'isEnrolled.value', _.get(policyChangeVM, 'baseData.isDriveChangeEnrolledInOtherPoliciesForPNI_WMIC.value'))
                }

                if (_.get(policyChangeVM, 'baseData.isDriveChangePriorCarrierDiscountAvailableInOtherPoliciesForPNI_WMIC.value')) {
                    _.set(driveChangeVM, 'previousDiscountPercentage.value', _.get(policyChangeVM, 'baseData.isDriveChangePriorCarrierDiscountAvailableInOtherPoliciesForPNI_WMIC.value'))
                }
            }

            _.set(driverVM, 'driveChange_WMIC', driveChangeVM)
        }

        _.set(driverVM, 'commercialDriver_WMIC', commercialDriverVM);
        _.set(driverVM, 'policyPeriodEndDate_WMIC', policyChangeVM.baseData.periodEndDate.value);
        _.set(driverVM, 'isExistingInPreviousPeriodDriver_WMIC', false)

        if (namedInsured) {
            _.set(driverVM, 'assignment_WMIC.value', _.find(
                driverVM.assignment_WMIC.aspects.availableValues,
                (assignmentType) => assignmentType.code === CONSTANTS.ASSIGNMENT.ASSIGNED));

            if (policyChangeVM.baseData.primaryNamedInsured_WMIC.subtype.value === CONSTANTS.Person
                && policyChangeVM.baseData.primaryNamedInsured_WMIC.publicID.value === driverVM.person.publicID.value) {
                driverVM.creditConsentReceived = policyChangeVM.baseData.creditConsentReceived.value;
                driverVM.creditConsentDate = policyChangeVM.baseData.creditConsentDate.value;
                driverVM.personalInfoConsentForm = (!_.isUndefined(policyChangeVM.baseData.personalInfoConsentForm.value) ?
                    policyChangeVM.baseData.personalInfoConsentForm.value : null);
                driverVM.creditInfoWithdrawalConsent = (!_.isUndefined(policyChangeVM.baseData.creditInfoWithdrawalConsent.value) ?
                    policyChangeVM.baseData.creditInfoWithdrawalConsent.value : null);
            }

            const ANI = _.find(policyChangeVM.baseData.additionalNamedInsureds_WMIC.value, (namedInsuredParam) => namedInsuredParam.contactPublicID === driverVM.person.publicID.value || namedInsuredParam.publicID === driverVM.person.publicID.value);

            if (ANI) {
                driverVM.creditConsentReceived = ANI.creditConsentReceived;
                driverVM.creditConsentDate = ANI.creditConsentDate;
                driverVM.personalInfoConsentForm = (!_.isUndefined(ANI.personalInfoConsentForm) ? ANI.personalInfoConsentForm : null);
                driverVM.creditInfoWithdrawalConsent = (!_.isUndefined(ANI.creditInfoWithdrawalConsent) ? ANI.creditInfoWithdrawalConsent : null);
                driverVM.person.publicID = ANI.contactPublicID;
            }

            if (policyChangeVM.baseData.primaryNamedInsured_WMIC.publicID.value === driverVM.person.publicID.value) {
                _.set(driverVM, 'priInsuredRelation_wmic.value', CONSTANTS.PRIMARY_INSURED_RELATIONSHIP.INSURED)
            } else {
                _.set(driverVM, 'priInsuredRelation_wmic.value', namedInsured.relationshipToPrimaryInsured_WMIC)
            }
        }

        policyChangeVM.lobData.personalAuto.coverables.drivers.value.push(driverVM.value);

        return driverVM;
        // Cannot include updateWizardData in the dependency array since calling it causes a new function to be created, which would cause in infinite loop in the useEffect below
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [maintainFurtherStepsVisitedSubmitted, policyChangeVM, skeletonStruct, viewModelService, setIsCustomStepsInvalidated]);

    const saveEndorsement = useCallback(async (selectedDriver, newPolicyChange, onError = _.noop) => {

        _.unset(newPolicyChange, 'bindData');

        try {
            WMICCreditConsentUtil.syncBaseDataToDriverLobData(selectedDriver, newPolicyChange.baseData);

            setWizardLoading(true, translator(messages.savingDriver));

            const result = await EndorsementService.saveEndorsement(
                [newPolicyChange],
                authHeader
            );

            _.extend(newPolicyChange, result);
            policyChangeVM.value = newPolicyChange;

            updateWizardData(policyChangeVM);
            setShowErrors(false);

            return true;
        } catch (error) {
            WMICLogger.error('Save driver failed', error);
            onError();

            return false;
        } finally {
            _.set(policyChangeVM, 'isEditingPage.value', false);
            setWizardLoading(false);
        }

    }, [setWizardLoading, translator, EndorsementService, authHeader, policyChangeVM, updateWizardData])


    const onSaveDriver = useCallback((selectedDriver) => saveEndorsement(selectedDriver.value, policyChangeVM.value), [policyChangeVM.value, saveEndorsement]);

    const onSaveDriverMiddleware = useCallback((selectedDriver) => {
        if (driversWithDuplicateLicence ) {
            const otherDriverWithSameLicense = driversWithDuplicateLicence .find(d => d.publicID !== selectedDriver?.value?.publicID) || driversWithDuplicateLicence [0];

            showError({
                title: translator(messages.licenseNotUnique),
                message: translator(messages.licenceMatchMsg, {driver: otherDriverWithSameLicense.displayName})
            })
        }
    }, [driversWithDuplicateLicence , showError, translator]);

    const deleteDriver = useCallback(async (driverId) => {

        _.unset(policyChangeVM.value, 'bindData');

        try {
            setWizardLoading(true, translator(messages.removingDriver));

            const removeDriverResult = await EndorsementService.removeDriver(
                policyChangeVM.jobID.value,
                driverId,
                authHeader
            );

            if (!_.isEmpty(removeDriverResult.errorMessages)) {
                showError({
                    title: translator(messages.removingDriverErrorModalTitle),
                    message: removeDriverResult.errorMessages.join('\n')
                });
                return false;
            }

            policyChangeVM.value = removeDriverResult.policyChangeData;

            updateWizardData(policyChangeVM);
            setShowErrors(false);

            return true;
        } catch (error) {
            WMICLogger.error('Remove driver failed', error);
            WMICErrorHandler.processAsModal(error, policyChangeVM.value.jobID, policyChangeVM.value.baseData.jobType);
            return false;
        } finally {
            _.set(policyChangeVM, 'isEditingPage.value', false);
            setWizardLoading(false);
        }
    }, [setWizardLoading, translator, EndorsementService, authHeader, showError, policyChangeVM, updateWizardData]);


    const onDeleteDriver = useCallback(driver => {
        const modalMsg = translator(messages.removeDriver, { driver: driver.displayName?.value });

        showConfirm({
            title: messages.removeDriverTitle,
            message: modalMsg,
            confirmButtonText: commonMessages.yes,
            cancelButtonText: commonMessages.noModel
        }).then(async (response) => {
            if (response === CONSTANTS.MODAL_RESULT.CONFIRM) {
                return deleteDriver(driver.publicID?.value);
            }
        }).catch(_.noop);
    }, [translator, showConfirm, deleteDriver]);

    const onCancelDriver = () => {
        _.set(policyChangeVM, 'isEditingPage.value', false);
        updateWizardData(policyChangeVM);
    }

    const onNext = useCallback(async () => {
        if (!isComponentValid || !WMICDriverUtil.presentDriverList(policyChangeVM)) {
            setShowErrors(true);
            return false;
        }

        if (canEditPolicyChange) {
            try {
                setWizardLoading(true);

                policyChangeVM.value = await EndorsementService.saveEndorsement(
                    [policyChangeVM.value],
                    authHeader
                );

                updateWizardData(policyChangeVM);

                return policyChangeVM;

            } finally {
                setWizardLoading(false);
            }
        }

        return false;
    }, [isComponentValid,  canEditPolicyChange, setWizardLoading, policyChangeVM, EndorsementService, authHeader, updateWizardData]);


    const renderAddDriverButton = useCallback(({ isEditing, onClick }) => {
        const drivers = _.get(policyChangeVM, `${DRIVERS_PATH}.value`, []);
        const pni = _.get(policyChangeVM, 'baseData.primaryNamedInsured_WMIC.value');

        return (<DropdownMenuButton
            id="addNewDriverButton"
            buttonText={translator({
                "id": "quoteandbind.pa.views.pa-drivers-details.addNewDriver",
                "defaultMessage": "meta.Add New Driver"
            })}
            icon="mi-add"
            disabled={isEditing} >
            {WMICDriverUtil.canContactBeAddedToDrivers(drivers, pni) && <DropdownMenuLink
                onClick={() => onClick(
                    drivers.length,
                    pni,
                    INSURED_ROLES.PRIMARY_INSURED)}>
                {pni.displayName}
            </DropdownMenuLink>}
            {additionalNamedInsureds.map((ani, index) => (
                WMICDriverUtil.canContactBeAddedToDrivers(drivers, ani.value) && <DropdownMenuLink
                    onClick={() => onClick(
                        drivers.length,
                        _.get(policyChangeVM, `${ANIS_PATH}.value[${index}]`),
                        INSURED_ROLES.ADDITIONAL_INSURED)}>
                    {ani.displayName.value}
                </DropdownMenuLink>
            ))}
            <DropdownMenuLink onClick={() => onClick(
                _.get(policyChangeVM, `${DRIVERS_PATH}.value`, []).length
            )}>{translator(messages.addNewDriver)}</DropdownMenuLink>
        </DropdownMenuButton>)
    }, [additionalNamedInsureds, policyChangeVM, translator]);

    const overrideProps = {
        atLeastOneMsgContainer: {
            visible: _.isEmpty(_.get(policyChangeVM, `${DRIVERS_PATH}.value`, [])),
            message: messages.atLeastOne
        },
        requiredText: {
            visible: _.get(policyChangeVM, DRIVERS_PATH).length > 0
        },
        driverListView: {
            value: _.get(policyChangeVM, `${DRIVERS_PATH}.children`, []),
            clickable: true,
            startOpen: true,
            readOnly: !canEditPolicyChange,
            VMData: [{
                headerText: translator(messages.driverName), path: 'displayName'
            },
            {
                headerText: translator(messages.licenseNumber), path: 'licenseNumber'
            },
            {
                headerText: translator(messages.licenseProvince), path: 'licenseState', getData: (row, path) => _.get(row.value, path) || translator(messages.licenseProvinceOther)
            }],
            toCreate: onCreateDriver,
            toEdit: onEditDriver,
            onSave: onSaveDriver,
            onSaveMiddleware: onSaveDriverMiddleware,
            onDelete: onDeleteDriver,
            toUndoCreate: () => {
                const drivers = _.get(policyChangeVM.value, DRIVERS_PATH);

                drivers.splice(drivers.length - 1, 1);
                _.set(policyChangeVM.value, DRIVERS_PATH, drivers);
                _.set(policyChangeVM, 'isEditingPage.value', false);
                updateWizardData(policyChangeVM);
            },
            detailViewComponent: WMICDriverDetailView,
            onValidate,
            renderAddButton: renderAddDriverButton,
            detailViewComponentProps: {
                jobVM: policyChangeVM
            },
            onCancel: onCancelDriver,
            showCancelModal: true
        },
    };

    return (
        <WMICWizardChangeOrRenewalPage
            onNext={onNext}
            cancelLabel={translator(policyChangeCommonMessages.saveAndExit)}
            isSkipping={initialValidation}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={policyChangeVM}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                showErrors={showErrors}
                onValidationChange={onValidate}
            />
        </WMICWizardChangeOrRenewalPage>
    );
}

WMICPCPADriversDetailsPage.propTypes = wizardProps;

export default withViewModelService(WMICPCPADriversDetailsPage);
